import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { TranslationsFunc, useTranslations } from '@lang';
import { usePaymentTermsLabel } from '@common/hooks/usePaymentTermsLabel';
import { useRates, useRatePageSettings } from '@features/rates';
import { RatesFilterType } from '@features/rates/types';
import { useRoomPageSettings, useStaticRooms } from '@features/rooms';
import { getLargestRoomOccupancy } from '@features/rooms/helpers/getLargestRoomOccupancy.helper';
import { usePackagesSpaAvailability } from '@features/spa/hooks/useGetPackagesSpaAvailability';
import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import { AccommodationListItem } from 'voyager';
import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';
import { Rate } from '@features/rates/types';
import {
  getPackageInclusions,
  inclusionLabelKeys,
  getRateInclusions,
  PackageInclusion,
} from '@features/packages/utils/getPackageInclusions';
import { getRateQuickView } from '@features/rates/helpers/getRateQuickView.helper';
import { fitsPackageStayLength } from '@features/packages/utils/fitsPackageStayLength';
import moment from 'moment';

const inclusionOrder: PackageInclusion[] = ['room', 'spa', 'dining'];

export const markPackagesOutsideStayLength = (rates: Rate[], stayNights: number): Rate[] =>
  rates
    .map((rate) =>
      fitsPackageStayLength(rate.package, stayNights) ? rate : { ...rate, unAvailable: true }
    )
    .sort((a, b) => Number(!!a.unAvailable) - Number(!!b.unAvailable));

const inclusionSignature = (item: AccommodationListItem) => (item.inclusions ?? []).join('|');

export const getOfferedInclusions = (items: AccommodationListItem[]): PackageInclusion[] => {
  if (new Set(items.map(inclusionSignature)).size < 2) return [];

  const offered = new Set(items.flatMap((item) => item.inclusions ?? []));

  return inclusionOrder.filter((inclusion) => offered.has(inclusion));
};

export const mapRateToListItem = (
  rate: Rate,
  t: TranslationsFunc,
  fallbackImageUrl?: string,
  spaAvailability?: { isUnavailable: boolean; isCheckFailed?: boolean },
  priceNote?: string | null
): AccommodationListItem => {
  const isSpaSoldOut = !!spaAvailability?.isUnavailable && !spaAvailability.isCheckFailed;
  const unavailable = rate.unAvailable || isSpaSoldOut;

  return {
    id: rate.id,
    name: rate.title,
    imageUrl: rate.imageUrl || fallbackImageUrl || undefined,
    price:
      rate.price && !unavailable
        ? { amount: rate.price.amount, currencyCode: rate.price.currencyCode }
        : undefined,
    priceNote: priceNote ?? undefined,
    badges: getPackageInclusions(rate.package).map((inclusion) => t(inclusionLabelKeys[inclusion])),
    inclusions: getRateInclusions(rate.package),
    unavailable,
    unavailableLabel: t('common.unavailable'),
    quickView: getRateQuickView(rate, t),
  };
};

export function useRatesList(
  journeyParams: ParsedJourneyParams,
  language: string,
  property: Property,
  type?: RatesFilterType
) {
  const { t } = useTranslations();
  const router = useRouter();
  const getPaymentTermsLabel = usePaymentTermsLabel();

  const isSpaStays = type === RatesFilterType.SPA_STAYS;

  const { content: ratesContent, isFetching: isFetchingRates } = useRatePageSettings(
    language,
    t('common.rate')
  );
  const { content: roomsContent, isFetching: isFetchingRooms } = useRoomPageSettings(
    language,
    t('common.rate')
  );

  const promoRateCode =
    queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode;

  const adults = journeyParams.adults ?? 0;
  const children = journeyParams.children ?? 0;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const {
    rates: availableRates,
    isFetching: isFetchingRateData,
    error,
    isAvailabilityUnknown,
    isRetryingAvailability,
    retryAvailability,
  } = useRates({
    fromDate: parseDateToString(journeyParams.fromDate),
    toDate: parseDateToString(journeyParams.toDate),
    adults,
    children,
    promoRateCode,
    childAges: journeyParams.childAges,
    rooms,
    type,
    language,
    availabilityMode: 'listing',
  });

  const { data: staticRooms, isFetching: isFetchingStaticRooms } = useStaticRooms();
  const largestRoomOccupancy = getLargestRoomOccupancy(staticRooms?.data ?? []);
  const parties = rooms.length > 1 ? rooms : [{ adults, children }];
  const isPartyTooLarge =
    largestRoomOccupancy !== null &&
    availableRates.every((rate) => rate.unAvailable) &&
    parties.some((party) => party.adults + party.children > largestRoomOccupancy);

  const stayNights = moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');
  const rates = markPackagesOutsideStayLength(availableRates, stayNights);

  // A spa stay is only sellable when the spa side of the package still has a
  // timeslot, which the rate availability response does not know about.
  const { data: spaAvailability, isFetching: isFetchingSpaAvailability } =
    usePackagesSpaAvailability(
      property,
      rates,
      journeyParams,
      isSpaStays && !!property?.id && !isFetchingRateData
    );

  const fallbackImageUrl =
    ratesContent?.heroBackgroundImage ?? roomsContent?.heroBackgroundImage ?? undefined;

  const listItems: AccommodationListItem[] = rates.map((rate) =>
    mapRateToListItem(
      rate,
      t,
      fallbackImageUrl ?? undefined,
      spaAvailability?.[rate.id] ?? undefined,
      getPaymentTermsLabel(rate.paymentTerms)
    )
  );

  const inclusionOptions = getOfferedInclusions(listItems).map((inclusion) => ({
    value: inclusion,
    label: t(inclusionLabelKeys[inclusion]),
  }));

  return {
    listItems,
    inclusionOptions,
    content: ratesContent,
    fallbackTitle: roomsContent?.title,
    isFetching:
      isFetchingRateData ||
      isFetchingRates ||
      isFetchingRooms ||
      isFetchingStaticRooms ||
      isFetchingSpaAvailability,
    error,
    isAvailabilityUnknown,
    isPartyTooLarge,
    largestRoomOccupancy,
    isRetrying: isRetryingAvailability,
    retry: retryAvailability,
  };
}
